import React from 'react';
import PropTypes from 'prop-types';
import styles from './Page.module.css';

export class Page extends React.Component {

	containerRef = React.createRef();
	imageRef = React.createRef();
	canvasRef = React.createRef();

	state = {
		width: 0,
		height: 0,
		ctx: null,
	};

	componentDidMount() {
		const { blocks } = this.props;
		if (!blocks) {
			return;
		}
		const containerRef = this.containerRef.current;
		const imageRef = this.imageRef.current;

		// Load container
		const observer = new ResizeObserver(this.onResize);
		observer.observe(containerRef, {
			attributes: true,
			attributeOldValue: true,
			attributeFilter: ['style'],
		});
		containerRef.addEventListener('resize', this.resizeListener);

		// Load canvas
		imageRef.onload = this.loadBlocks;
		/*this.setState({
			width: clientWidth,
			height: clientHeight,
		});*/
	}

	componentDidUpdate(prevProps, prevState) {
		const { width, height } = this.state;
		if (prevState.width !== width || prevState.height !== height) {
			this.loadBlocks();
		}
	}

	componentWillUnmount() {
		const containerRef = this.containerRef.current;
		containerRef.removeEventListener('resize', this.resizeListener, false);
	}

	loadBlocks = () => {
		const { blocks } = this.props;
		if (!blocks) {
			return;
		}

		const imageRef = this.imageRef.current;
		const canvas = this.canvasRef.current;
		if(!canvas) {
			return;
		}
		const ctx = canvas.getContext('2d');

		ctx.canvas.width = imageRef.offsetWidth;
		ctx.canvas.height = imageRef.offsetHeight;

		// Reset the canvas
		ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

		const blockRenderer = new BlockRenderer({
			articles: blocks,
			width: imageRef.offsetWidth,
			height: imageRef.offsetHeight,
		});

		for (const block of blockRenderer.blocks) {
			const { color, coords } = block;
			let x = coords[0];
			let y = coords[1];
			let width = coords[2] - x;
			let height = coords[3] - y;
			ctx.fillStyle = color;
			ctx.clearRect(x, y, width, height);
			ctx.fillRect(x, y, width, height);
			ctx.closePath();
		}

		ctx.save();

		this.setState({
			ctx,
			blockRenderer,
		});
	};

	onResize = (mutations) => {
		const el = mutations[0].target;
		const w = el.clientWidth;
		const h = el.clientHeight;

		const isChange = mutations
			.map((m) => `${m.oldValue}`)
			.some(
				(prev) =>
					prev.indexOf(`width: ${w}px`) === -1 ||
					prev.indexOf(`height: ${h}px`) === -1
			);

		if (!isChange) {
			return;
		}
		const event = new CustomEvent('resize', {
			detail: { width: w, height: h },
		});
		el.dispatchEvent(event);
	};

	resizeListener = (e) => {
		const { width, height } = e.detail;
		this.setState({
			width,
			height,
		});
	};

	onImageClick = (e) => {
		const { onSelect } = this.props;
		const { ctx, blockRenderer } = this.state;
		if (!ctx) {
			return;
		}

		if (!blockRenderer) {
			return;
		}

		const rect = e.target.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const point = ctx.getImageData(x, y, 1, 1).data;
		const isABox = point[3];
		const color = `#${this.rgbToHex(point[0], point[1], point[2])}`;
		if (isABox) {
			const article = blockRenderer.article.get(color);
			if (article) {
				onSelect(article);
			}
			return;
		}

		onSelect(
			blockRenderer.getNearestBlock({
				x,
				y,
			})
		);
	};

	rgbToHex(r, g, b) {
		if (r > 255 || g > 255 || b > 255) throw 'Invalid color component';
		return ((r << 16) | (g << 8) | b).toString(16);
	}

	render() {
		const { image, blocks, article, onSelect } = this.props;
		const onClick = () => {
			onSelect(article);
		}
		const style = {
			cursor: 'pointer'
		};
		return (
			<div className={styles['page']} ref={this.containerRef}>
				<img
					src={image}
					ref={this.imageRef}
					onDragStart={(e) => { e.preventDefault() }}
					onClick={blocks ? this.onImageClick : onClick}
					style={style}
				/>
				<canvas ref={this.canvasRef} />
			</div>
		);
	}
}

Page.propTypes = {
	article: PropTypes.string,
	image: PropTypes.string,
	blocks: PropTypes.object,
	onSelect: PropTypes.func,
	disableDrag: PropTypes.bool
};

class BlockRenderer {
	color = new Map();
	article = new Map();
	width = 0;
	height = 0;
	blocks = [];
	constructor({ articles, width, height }) {
		let colorSet = new Set();

		this.width = width;
		this.height = height;

		while (colorSet.size < Object.keys(articles).length) {
			colorSet.add(this.getColor());
		}

		const colors = Array.from(colorSet);

		for (const id in articles) {
			const color = colors.shift();
			this.color.set(id, color);
			this.article.set(color, id);
			this.addBlocks(id, articles[id]);
		}
	}

	getNearestBlock = ({ x, y }) => {
		const blocks = this.blocks.map(this.toCoordinates);
		let distance = 0;
		let point = null;
		let article = null;
		for (const block of blocks) {
			for (const p of block.points) {
				if (!point) {
					point = p;
					distance = Math.sqrt(
						Math.pow(x - p.x, 2) + Math.pow(y - p.y, 2)
					);
					article = block.article;
					continue;
				}

				const newDistance = Math.sqrt(
					Math.pow(x - p.x, 2) + Math.pow(y - p.y, 2)
				);
				if (newDistance < distance) {
					distance = newDistance;
					point = p;
					article = block.article;
				}
			}
		}
		return article;
	};

	toCoordinates(item) {
		const { coords } = item;

		item.points = [
			{
				x: coords[0],
				y: coords[1],
			},
			{
				x: coords[0],
				y: coords[3],
			},
			{
				x: coords[2],
				y: coords[1],
			},
			{
				x: coords[2],
				y: coords[3],
			},
		];

		return item;
	}

	addBlocks = (id, blocks) => {
		for (const coords of blocks) {
			const top = coords[1];
			const left = coords[0];
			const bottom = coords[3];
			const right = coords[2];
			this.blocks.push({
				article: id,
				color: this.color.get(id),
				coords: [
					left * this.width,
					top * this.height,
					right * this.width,
					bottom * this.height,
				],
			});
		}
	};

	getColor = () => {
		const color = Math.floor(Math.random() * 16777215).toString(16);
		return `#${color}`;
	};
}
